import { makeAutoObservable } from 'mobx';

import { fetchBankStat, fetchMausoleumBanks } from 'src/api';
import { BankTypeShown, Rights } from 'src/constants';
import type { IBank, IBankStat, IMausoleumBankStatRow } from 'src/interfaces';

interface IBankPopupPosition {
  x: number;
  y: number;
}

class MausoleumStore {
  isBanksLoading = false;

  private banks: IBank[] = [];
  private isStatLoading = false;
  private selectedBank: IBank | null = null;
  private selectedBankPosition: IBankPopupPosition | null = null;
  private selectedBankStatRows: IMausoleumBankStatRow[] = [];

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  private setBanks(banks: IBank[]) {
    this.banks = banks;
  }

  private setBanksLoading(isLoading: boolean) {
    this.isBanksLoading = isLoading;
  }

  private setSelectedBank(bank: IBank | null) {
    this.selectedBank = bank;
  }

  private setSelectedBankPosition(position: IBankPopupPosition | null) {
    this.selectedBankPosition = position;
  }

  private setSelectedBankStatRows(statRows: IMausoleumBankStatRow[]) {
    this.selectedBankStatRows = statRows;
  }

  private setStatLoading(isLoading: boolean) {
    this.isStatLoading = isLoading;
  }

  get banksWithPlans() {
    return this.banks.map((bank) => ({ bank, plan: bank.plan || '' })).filter(({ plan }) => !!plan);
  }

  get bankModal() {
    if (!this.selectedBank || !this.selectedBankPosition) {
      return null;
    }

    return {
      bank: this.selectedBank,
      bankTypeLabel: this.getBankTypeLabel(this.selectedBank),
      isLoading: this.isStatLoading,
      position: this.selectedBankPosition,
      statRows: this.selectedBankStatRows,
    };
  }

  resetSelectedBank() {
    this.setSelectedBank(null);
    this.setSelectedBankPosition(null);
    this.setSelectedBankStatRows([]);
    this.setStatLoading(false);
  }

  async selectBank(bank: IBank, position: IBankPopupPosition) {
    this.setSelectedBank(bank);
    this.setSelectedBankPosition(position);
    this.setSelectedBankStatRows([]);
    this.setStatLoading(true);

    const bankStat = await fetchBankStat(bank.id);

    if (this.selectedBank?.id !== bank.id) {
      return;
    }

    this.setSelectedBankStatRows(bankStat ? this.getBankStatRows(bankStat) : []);
    this.setStatLoading(false);
  }

  async loadBanks(mausoleumId: string) {
    this.setBanksLoading(true);
    this.resetSelectedBank();

    const loadedBanks = await fetchMausoleumBanks(mausoleumId);
    this.setBanks(loadedBanks || []);
    this.setBanksLoading(false);
  }

  private getBankTypeLabel(bank: IBank) {
    if (!bank.bank_type) {
      return '';
    }

    return BankTypeShown[bank.bank_type] || bank.bank_type;
  }

  private getBankStatRows(bankStat: IBankStat): IMausoleumBankStatRow[] {
    return Object.entries(bankStat).map(([right, stat]) => ({
      all: stat.all,
      available: stat.available,
      id: right,
      label: Rights[right] || right,
    }));
  }
}

export default MausoleumStore;
